import { Component, Inject, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AUserPermissionsService, handleError, HighAvailabilityModes, IConfigurationHolder, ISharePointGroupHolder, isRunning, ReplicationService, SettingsService } from "@coso-replicator/core";
import { appendRowNodeIdToInputAriaLabel } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { IS_SAAS } from "@coso/utility";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ColDef, GridOptions, RowNode, SelectionChangedEvent } from "ag-grid-community";
import { forkJoin } from "rxjs";
import { GroupsService } from "../../../../services/groups.service";
import { AddGroupComponent } from "./add-group/add-group.component";

@Component({
  standalone:false,
  templateUrl: "./groups.component.html",
  styleUrls: ["./groups.component.scss"],
})
export class GroupsComponent implements OnInit {
  instanceId: string;
  sharePointId: number;
  isFailoverBackupInstance: boolean;
  isReadOnly: boolean;
  isRunning: boolean;
  gridOptions: GridOptions;
  columnDefs: ColDef[];
  rowData: ISharePointGroupHolder[] = [];
  selectedRows: ISharePointGroupHolder[] = [];
  configuration: IConfigurationHolder;
  addModalRef: NgbModalRef;
  allSharepointGroups: ISharePointGroupHolder[];
  sharePointGroups: ISharePointGroupHolder[];
  errorMessage = undefined;
  successMessage = undefined;
  initFailed = false;

  constructor(
    @Inject(IS_SAAS)
    public isSaaS: boolean,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    public loadingService: LoadingService,
    private groupsService: GroupsService,
    private replicationService: ReplicationService,
    private userPermissionsService: AUserPermissionsService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.params["instance-id"];
    this.sharePointId = this.route.snapshot.params["sharepoint-id"];

    this.gridOptions = {
      rowHeight: 40,
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      isRowSelectable: this.isRowSelectable.bind(this),
      onSelectionChanged: this.onSelectionChanged.bind(this),
      onFirstDataRendered: (e) => {
        e.api.setGridAriaProperty("label", "List of groups");
        e.api.forEachNode(appendRowNodeIdToInputAriaLabel);
      }
    };

    this.columnDefs = [
      {
        headerName: "Id",
        field: "id",
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true
      },
      {
        headerName: "Name",
        field: "name"
      }
    ];

    this.loadInformation();
  }

  private onSelectionChanged(event: SelectionChangedEvent) {
    const rows = event.api.getSelectedRows();
    this.selectedRows = Object.assign([], rows);
  }

  private isRowSelectable(_event: RowNode) {
    return !this.isReadOnly;
  }

  openAddModal() {
    this.addModalRef = this.modalService.open(AddGroupComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: "modal-lg"
    });
    this.addModalRef.componentInstance.sharePointGroups = this.sharePointGroups;
    this.addModalRef.componentInstance.saveCallback = this.save.bind(this);
  }

  private loadInformation() {
    this.loadingService.start("Loading groups", "loadGroups");

    forkJoin([
      this.replicationService.getStatus(this.instanceId),
      this.userPermissionsService.hasLimitedAccess$,
      this.groupsService.get(this.instanceId, this.sharePointId),
      this.settingsService.get(this.instanceId)
    ]).subscribe(
      ([status, hasLimitedAccess, groups, settings]) => {
        this.isReadOnly = hasLimitedAccess;
        this.isRunning = isRunning(status);
        if (this.isSaaS === false) {
          this.isFailoverBackupInstance = settings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
          this.isReadOnly = this.isReadOnly || this.isFailoverBackupInstance || this.isRunning;
        }

        this.allSharepointGroups = groups;
        this.updateInformation(settings);
        this.configuration = settings;

        this.loadingService.stop("loadGroups");
      },
      (error) => {
        this.initFailed = true;
        this.rowData = [];
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadGroups");
      }
    );
  }

  private save(entity: ISharePointGroupHolder) {
    this.loadingService.start("Adding new group", "saveSettings");
    let spConfig = this.configuration.sharePoint.sharepointConfig.filter((value) => value.key == this.sharePointId)[0];
    if (spConfig.userCreationGroups === null) {
      spConfig.userCreationGroups = [];
    }
    spConfig.userCreationGroups.push(entity);
    this.settingsService.save(this.instanceId, this.configuration).subscribe(
      (settings) => {
        this.configuration = settings;
        this.updateInformation(settings);
        this.successMessage = "Group has been successfully added.";
        this.addModalRef.close();
        this.loadingService.stop("saveSettings");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("saveSettings");
      }
    );
  }

  removeSelected() {
    this.loadingService.start("Removing selected groups", "removeSelected");
    this.selectedRows.map((row) => {
      let index = this.configuration.sharePoint.sharepointConfig.filter((value) => value.key == this.sharePointId)[0].userCreationGroups.findIndex((group) => group.id === row.id);
      this.configuration.sharePoint.sharepointConfig.filter((value) => value.key == this.sharePointId)[0].userCreationGroups.splice(index, 1);
    });
    this.settingsService.save(this.instanceId, this.configuration).subscribe(
      (settings) => {
        this.configuration = settings;
        this.updateInformation(settings);
        this.selectedRows = [];
        this.successMessage = "Selected groups have been successfully removed.";
        this.loadingService.stop("removeSelected");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("removeSelected");
      }
    );
  }

  private updateInformation(settings: IConfigurationHolder) {
    this.rowData = settings.sharePoint.sharepointConfig.filter((value) => value.key == this.sharePointId)[0].userCreationGroups;

    if (this.rowData === null) {
      this.rowData = [];
    }

    this.sharePointGroups = this.allSharepointGroups.filter((value) => !this.rowData.some((row) => row.id == value.id));
  }
}
